import type { MemberAddress } from "@/lib/queries/member-data";
import { RadioGroup, RadioGroupItem } from "@repo/ui";
import { useMemo } from "react";

export interface ProfileAddress {
  id: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  isPrimary: boolean;
}

interface AddressSelectorProps {
  profileAddresses: ProfileAddress[];
  selectedAddressId: string;
  onAddressChange: (address: MemberAddress) => void;
}

export function AddressSelector({
  profileAddresses,
  selectedAddressId,
  onAddressChange,
}: AddressSelectorProps) {
  // Deduplicate addresses by field values (DB may have duplicate rows with different IDs),
  // then sort so isPrimary=true comes first.
  const uniqueAddresses = useMemo(() => {
    const seen = new Set<string>();
    const deduped = profileAddresses.filter((addr) => {
      const key = [
        addr.line1,
        addr.line2,
        addr.city,
        addr.state,
        addr.zip,
        addr.country,
      ].join("|");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return [...deduped].sort(
      (a, b) => Number(b.isPrimary) - Number(a.isPrimary),
    );
  }, [profileAddresses]);

  // Ensure selectedAddressId maps to an address that survived deduplication.
  // If the original ID was on a duplicate row that was removed, fall back to
  // the first unique address with matching fields, or the first address.
  const effectiveSelectedId = useMemo(() => {
    if (uniqueAddresses.some((a) => a.id === selectedAddressId))
      return selectedAddressId;
    if (selectedAddressId === "new") return "new";
    return uniqueAddresses[0]?.id ?? "new";
  }, [uniqueAddresses, selectedAddressId]);

  return (
    <RadioGroup
      value={effectiveSelectedId}
      onValueChange={(val: string) => {
        if (val === "new") {
          onAddressChange({
            firstName: "",
            lastName: "",
            address1: "",
            address2: "",
            city: "",
            country: "",
            zip: "",
            phone: "",
            province: "",
          });
        } else {
          const selected = uniqueAddresses.find((a) => a.id === val);
          if (selected) {
            onAddressChange({
              firstName: selected.firstName || "",
              lastName: selected.lastName || "",
              address1: selected.line1 || "",
              address2: selected.line2 || "",
              city: selected.city || "",
              country: selected.country || "",
              province: selected.state || "",
              zip: selected.zip || "",
              phone: selected.phone || "",
            });
          }
        }
      }}
    >
      {uniqueAddresses.map((addr, i) => {
        const label =
          i === 0
            ? "Primary Address"
            : i === 1
              ? "Secondary Address"
              : `Address ${i + 1}`;
        return (
          <div
            key={addr.id}
            className="flex items-center space-x-2 border rounded-md p-3 hover:bg-accent/50 transition-colors"
          >
            <RadioGroupItem value={addr.id} id={addr.id} />
            <label
              htmlFor={addr.id}
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer"
            >
              <div className="flex justify-between items-center">
                <span>{label}</span>
                {i === 0 && (
                  <span className="text-[10px] bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300 px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold">
                    Default
                  </span>
                )}
              </div>
              <p className="text-muted-foreground text-xs mt-1 font-normal line-clamp-1">
                {[addr.line1, addr.line2, addr.city, addr.state, addr.zip]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            </label>
          </div>
        );
      })}
      <div className="flex items-center space-x-2 border rounded-md p-3 hover:bg-accent/50 transition-colors">
        <RadioGroupItem value="new" id="new-address" />
        <label
          htmlFor="new-address"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer"
        >
          Enter New Address
        </label>
      </div>
    </RadioGroup>
  );
}
